import { cn } from "../lib/cn";

export interface MyDailyDirectLogoProps {
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  showText?: boolean;
  title?: string;
  subtitle?: string;
  className?: string;
  iconOnly?: boolean;
}

export type TradoLogoProps = MyDailyDirectLogoProps;

export function MyDailyDirectLogo({
  size = "md",
  showText = true,
  title = "MyDailyDirect",
  subtitle,
  className,
  iconOnly = false,
}: MyDailyDirectLogoProps) {
  const iconSizes = {
    sm: "h-6 w-6 rounded-md",
    md: "h-8 w-8 rounded-lg",
    lg: "h-11 w-11 rounded-xl",
    xl: "h-16 w-16 rounded-2xl shadow-md",
    "2xl": "h-22 w-22 rounded-2xl shadow-lg ring-2 ring-emerald-500/30",
    "3xl": "h-28 w-28 rounded-3xl shadow-xl ring-4 ring-emerald-500/40",
    "4xl": "h-36 w-36 rounded-3xl shadow-2xl ring-4 ring-primary/40",
  };

  const textSizes = {
    sm: "text-xs font-bold whitespace-nowrap",
    md: "text-sm font-extrabold tracking-tight whitespace-nowrap",
    lg: "text-base font-black tracking-tight whitespace-nowrap",
    xl: "text-xl font-black tracking-tight whitespace-nowrap",
    "2xl": "text-2xl font-black tracking-tight whitespace-nowrap",
    "3xl": "text-3xl font-black tracking-tight whitespace-nowrap",
    "4xl": "text-4xl font-black tracking-tight whitespace-nowrap",
  };

  return (
    <div className={cn("inline-flex items-center gap-2.5 select-none max-w-full overflow-hidden", className)}>
      <div
        className={cn(
          "relative shrink-0 overflow-hidden shadow-lg ring-1 ring-emerald-500/50 transition-all duration-300 hover:scale-105 bg-slate-950",
          iconSizes[size]
        )}
      >
        <img
          src="/my-daily-direct-logo.jpg"
          alt="My Daily Direct Logo"
          className="h-full w-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/my-daily-direct-logo.jpg";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/20 via-transparent to-sky-400/25 pointer-events-none" />
      </div>

      {showText && !iconOnly && (
        <div className="flex flex-col leading-none min-w-0">
          <span
            className={cn(
              "font-extrabold tracking-tight bg-gradient-to-r from-emerald-400 via-teal-300 to-sky-400 bg-clip-text text-transparent drop-shadow-xs truncate whitespace-nowrap",
              textSizes[size]
            )}
            title={title}
          >
            {title}
          </span>
          {subtitle && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-0.5 truncate whitespace-nowrap">
              {subtitle}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export const TradoLogo = MyDailyDirectLogo;
